/** @jsx jsx */
import { Box, Button, Tiles, Inline, jsx } from '@front10/base-elements';
import { Icon } from '../../../packages/base-elements/Icon/Icon';
import { replaceAll, useTranslation, useIsMobileView } from '@front10/utils';
import { removeInvalidCharacters } from './../../../helpers/utils';
import VisuallyHidden from '@reach/visually-hidden';
import {
  addDays,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfToday,
  isSameDay,
  addMonths,
  differenceInMonths,
  differenceInDays,
  endOfMonth,
  min
} from 'date-fns';
import produce from 'immer';
import nanoid from 'nanoid';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../Checkbox';
import { DateRangePickerProps } from '../DateRangePicker';
import { usePopupFilter } from '../FlexibleFilterContainer';
import { Input } from '../Input';
import { Listbox } from '../Listbox';
import { MobileBody, MobileFilterDialog, MobileFooter, MobileHeader } from '../MobileFilterView';
import { formatDate } from '../utils';
import { DatePickerContext } from './datePickerContext';
import { useDatePickerI18n } from './datePickerI18n';
import { Month } from './Month';
import { Dropdown } from './Popover';
import {
  END_DATE,
  FocusedInput,
  getInitialMonths,
  OnDatesChangeProps,
  START_DATE,
  useDatePicker
} from './useDatePicker';
import React from 'react';
import { useBlackoutDates } from './../../lab/DateRangePicker/useDatePicker/useBlackoutDates/useBlackoutDates';

interface State {
  readonly startDate: Date | null;
  readonly endDate: Date | null;
  readonly startTime?: string;
  readonly endTime?: string;
  readonly focusedInput: FocusedInput;
  readonly isFlexibleDates: boolean;
  readonly numberOfMonths: number;
}

function isPast(date: Date) {
  return differenceInCalendarDays(Date.now(), date) > 0;
}

function getNumberOfMonth(maxDate: Date) {
  const newMaxDate = addDays(maxDate, -1);
  let monthDiff = 0;
  const initialDate = new Date(startOfToday().getFullYear(), startOfToday().getMonth(), 1);
  let tmpEndDate = initialDate;
  while (isBefore(tmpEndDate, newMaxDate) || isSameDay(tmpEndDate, newMaxDate)) {
    monthDiff++;
    tmpEndDate = addMonths(initialDate, monthDiff);
  }
  return monthDiff;
}

function DatePickerCmp(props: DateRangePickerProps, ref: any) {
  const translate = useTranslation();
  const isMobile = useIsMobileView();
  const fetchBlackoutDates = props?.fetchBlackoutDates ? props?.fetchBlackoutDates : null;
  const blackoutDatesIntegration = props?.blackoutDatesIntegration;
  const minDate =
    typeof props.minDate === 'string'
      ? parseISO(props.minDate)
      : addDays(startOfToday(), props.minDate ?? 0);
  let maxDate: Date | undefined = undefined;
  if (props.maxDate != null) {
    maxDate =
      typeof props.maxDate === 'string'
        ? parseISO(props.maxDate)
        : addDays(startOfToday(), props.maxDate - 1);
  }
  const [state, setState] = useState<State>({
    startDate: props.defaultValue?.start ?? null,
    endDate: props.defaultValue?.end ?? null,
    startTime: props.defaultValue?.startTime ?? props.timeOptions?.[0]?.value,
    endTime: props.defaultValue?.endTime ?? props.timeOptions?.[0]?.value,
    focusedInput: null,
    isFlexibleDates: props.flexibleDates ?? false,
    numberOfMonths: isMobile ? (maxDate ? getNumberOfMonth(maxDate) : 6) : 6
  });

  const { getMinBlackoutDate, getMaxBlackoutDate_ } = useBlackoutDates({
    startDate: state.startDate,
    maxRange: props.maxRange
  });

  React.useEffect(() => {
    if (props?.blackoutDatesIntegration === true && fetchBlackoutDates) {
      if (props?.singleSelection) {
        if (fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined) {
          if (fetchBlackoutDates?.isValidSelectedDepartureDate === false) {
            if (props?.setValue) {
              props?.setValue('dates', {});
            }
            setState({ ...state, startDate: null, endDate: null });
          } else {
            if (props?.setValue && props?.getValues && state.startDate) {
              props?.setValue('dates', { start: state.startDate, end: null });
              setState({ ...state, startDate: state.startDate, endDate: null });
            }
          }
        }
      } else {
        if (
          fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedDepartureDate === false &&
          fetchBlackoutDates?.isValidSelectedReturnDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedReturnDate === false
        ) {
          setState({ ...state, startDate: null, endDate: null });
        } else if (
          fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedDepartureDate === false
        ) {
          setState({ ...state, startDate: null });
        } else if (
          fetchBlackoutDates?.isValidSelectedReturnDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedReturnDate === false
        ) {
          setState({ ...state, endDate: null });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBlackoutDates]);

  React.useEffect(() => {
    if (props?.blackoutDatesIntegration === true) {
      if (props?.selectedOrigin?.value === '') {
        setState({ ...state, startDate: null, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.selectedOrigin]);

  React.useEffect(() => {
    if (props?.blackoutDatesIntegration === true) {
      if (props?.selectedDestination?.value === '') {
        setState({ ...state, startDate: null, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.selectedDestination]);

  const monthRef = useRef<HTMLDivElement>(null);
  const monthsToShow = props?.numberOfMonths ?? 2;
  const numberOfMonths = isMobile ? state.numberOfMonths : monthsToShow;

  useEffect(() => {
    props.setSelectedDates?.({ startDate: state.startDate, endDate: state.endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabledPreviousBtn = (month: number, year: number) => {
    if (
      props.enableAutomaticStatusNavBarButtons ||
      props.enableAutomaticStatusNavBarButtons === undefined
    ) {
      if (props.minDate !== undefined) {
        const minDate_ =
          blackoutDatesIntegration === true ? min([minBookingDate ?? minDate, minDate]) : minDate;
        if (year < minDate_.getFullYear()) {
          return true;
        } else if (year === minDate_.getFullYear()) {
          if (minDate_.getMonth() >= month) {
            return true;
          }
        }
      }
      return false;
    }
    return false;
  };

  /**
   * Increase numbered months from 1 to 12
   * @param month
   * @param year
   * @param amount
   * @return {month, year}
   */
  const increaseMonthYear = (
    month: number,
    year: number,
    amount: number
  ): { month: number; year: number } => {
    let nextMonth = month + amount;
    let yearInc = Math.floor((nextMonth > 12 ? nextMonth : 0) / 12);
    nextMonth = nextMonth % 12;
    nextMonth = nextMonth === 0 ? 12 : nextMonth;
    return { month: nextMonth, year: year + yearInc };
  };

  const isDisabledNextBtn = (month: number, year: number) => {
    const maxDate_ = blackoutDatesIntegration === true ? maxBookingDate : maxDate;
    if (props.enableAutomaticStatusNavBarButtons || !maxDate_) {
      return false;
    }
    const maxDateEpoch = maxDate_.getTime() / 1000;
    const rightMonthYear = increaseMonthYear(month, year, 2);
    const rightMonthLastDay = new Date(rightMonthYear.year, rightMonthYear.month, 0);
    const rightMonthLastDayEpoch = rightMonthLastDay.getTime() / 1000;
    return rightMonthLastDayEpoch >= maxDateEpoch - 24 * 3600;
  };

  const handleScroll = (e: any) => {
    if (maxDate) {
      let totalMonthsToShow = differenceInMonths(maxDate, startOfToday());
      if (differenceInDays(maxDate, endOfMonth(addMonths(startOfToday(), totalMonthsToShow))) > 0) {
        totalMonthsToShow++;
      }
      if (totalMonthsToShow > state.numberOfMonths) {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom) {
          if (totalMonthsToShow - state.numberOfMonths >= 3) {
            setState({ ...state, numberOfMonths: state.numberOfMonths + 3 });
          } else {
            setState({ ...state, numberOfMonths: totalMonthsToShow - state.numberOfMonths });
          }
        }
      }
    } else {
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      if (bottom) {
        setState({ ...state, numberOfMonths: state.numberOfMonths + 3 });
      }
    }
  };

  const mobileMonths = useMemo(() => getInitialMonths(numberOfMonths, startOfToday()), [
    numberOfMonths
  ]);
  const { parse = parseISO, format = formatDate } = useDatePickerI18n();
  const dateFormat = props.dateFormat ?? 'yyyy-MM-dd';
  const dateFormatOnEdit = props.dateFormatOnEdit ?? dateFormat;
  const labelDateFormat = removeInvalidCharacters(replaceAll(dateFormatOnEdit, ' ', '-'));
  const dateFormatToDescribe = translate(`date-format-${labelDateFormat}`, labelDateFormat);
  const getFormat = (isOnEdit: boolean) => (isOnEdit ? dateFormatOnEdit : dateFormat);

  function getEndDate() {
    if (props.singleSelection === null) {
      return null;
    } else {
      if (state.endDate === null) {
        return state.startDate;
      } else {
        return state.endDate;
      }
    }
  }

  const maxBookingDate = getMaxBlackoutDate_(
    maxDate,
    blackoutDatesIntegration,
    fetchBlackoutDates,
    state.focusedInput
  );

  const minBookingDate = getMinBlackoutDate(
    minDate,
    blackoutDatesIntegration,
    fetchBlackoutDates,
    state.focusedInput
  );

  React.useEffect(() => {
    if (state.focusedInput !== null && isMobile) {
      setState({
        ...state,
        numberOfMonths: getNumberOfMonth((maxBookingDate ?? maxDate) as any)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.focusedInput]);

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isStartDate,
    isEndDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToDate,
    onResetDates,
    goToNextMonthsByOneMonth,
    goToPreviousMonthsByOneMonth
  } = useDatePicker({
    startDate: state.startDate,
    endDate: getEndDate(),
    focusedInput: state.focusedInput,
    onDatesChange: handleDateChange,
    numberOfMonths: numberOfMonths,
    isDateBlocked: isPast,
    exactMinBookingDays: props.singleSelection,
    maxBookingDate: maxBookingDate,
    minBookingDate: minBookingDate,
    initialVisibleMonth: minDate,
    fetcheUnavailableDates: blackoutDatesIntegration ? fetchBlackoutDates : null,
    blackoutDatesIntegration: blackoutDatesIntegration
  });

  const popup = usePopupFilter();

  const months = isMobile ? mobileMonths : activeMonths;
  const requestFocus = useRef<'popup' | 'start' | 'end' | false>(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const [departureDescriptionId] = useState(() => nanoid());
  const [returnDescriptionId] = useState(() => nanoid());
  const [activeDone, setActiveDone] = useState(false);
  const [makeClearAction, setMakeClearAction] = React.useState(false);

  const dismiss = () => {
    setDialogOpen(false);
    setState({ ...state, focusedInput: null });
    setActiveDone(false);
  };

  const save = () => {
    setDialogOpen(false);
  };

  function setStartInput(date: Date | null | undefined, isOnEdit: boolean) {
    if (startRef.current) {
      startRef.current.value = date ? format(date, getFormat(isOnEdit)) : '';
    }
  }

  function setEndInput(date: Date | null | undefined, isOnEdit: boolean) {
    if (endRef.current) {
      endRef.current.value =
        date && !props.singleSelection ? format(date, getFormat(isOnEdit)) : '';
    }
  }

  function handleDateChange(data: OnDatesChangeProps & { shouldUpdate?: boolean }) {
    let { startDate, endDate } = data;
    if (
      (startDate !== null && startDate !== undefined) ||
      (endDate !== null && endDate !== undefined)
    ) {
      setMakeClearAction(false);
    }
    if (data.startDate && data.endDate && isAfter(data.startDate, data.endDate)) {
      endDate = data.startDate;
    }

    // If the selected start date makes the resulting range
    // to violate the maxRange prop reduce the end date to
    // the maximum value that still makes the range valid.
    if (data.focusedInput === END_DATE && props.maxRange && startDate && endDate) {
      const maxAllowedDate = addDays(startDate, props.maxRange - 1);
      if (isBefore(maxAllowedDate, endDate)) {
        endDate = maxAllowedDate;
      }
    }

    setState((oldState) =>
      produce(oldState, (draft) => {
        draft.focusedInput = data.focusedInput ?? START_DATE;
        draft.startDate = startDate;
        draft.endDate = endDate;
      })
    );

    const shouldUpdate = data.shouldUpdate ?? true;
    if (shouldUpdate) {
      let showFormat: boolean;
      if (data.focusedInput === START_DATE) {
        showFormat = true;
      } else if (data.focusedInput === null) {
        showFormat = true;
      } else {
        showFormat = false;
      }
      setStartInput(startDate, showFormat);
      setEndInput(endDate, data.focusedInput === END_DATE);
    }
  }

  useEffect(() => {
    if (state.focusedInput === null) {
      setStartInput(state.startDate, false);
      setEndInput(state.endDate, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.focusedInput, props.singleSelection]);

  useEffect(() => {
    if (blackoutDatesIntegration === true) {
      if (
        props.singleSelection &&
        fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
        fetchBlackoutDates?.isValidSelectedDepartureDate === false
      ) {
        setState({ ...state, startDate: null, endDate: null });
      }
    } else {
      if (state.endDate && props.singleSelection) {
        setState({ ...state, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.singleSelection]);

  useEffect(() => {
    props.onChange?.({
      start: state.startDate ?? undefined,
      end: state.endDate && !props.singleSelection ? state.endDate : undefined,
      startTime: state.startTime,
      endTime: state.endTime,
      isFlexibleDates: state.isFlexibleDates
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.endDate,
    state.endTime,
    state.startDate,
    state.startTime,
    props.singleSelection,
    state.isFlexibleDates,
    state.numberOfMonths
  ]);

  const getDisabledReturnStatus = () => {
    if (blackoutDatesIntegration) {
      if (
        fetchBlackoutDates !== null &&
        fetchBlackoutDates !== undefined &&
        props?.selectedOrigin &&
        props?.selectedOrigin?.value !== '' &&
        props?.selectedDestination &&
        props?.selectedDestination?.value !== '' &&
        props?.flightType !== 'ow'
      ) {
        return false;
      } else {
        return true;
      }
    }
    return props.singleSelection;
  };

  const getDisabledDepartureStatus = () => {
    if (blackoutDatesIntegration) {
      if (
        fetchBlackoutDates !== null &&
        fetchBlackoutDates !== undefined &&
        props?.selectedOrigin &&
        props?.selectedOrigin?.value !== '' &&
        props?.selectedDestination &&
        props?.selectedDestination?.value !== ''
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  };

  const inputs = (
    <Tiles
      gap={2}
      columns={props.columns ?? (props.showTimePickers ? [2, 2, 4] : 2)}
      ref={popup.filterRef}
    >
      <Input
        label={props.labels?.departure || 'Departure'}
        aria-describedby={departureDescriptionId}
        value={state.startDate ? format(state.startDate, dateFormat) : ''}
        placeholder={translate('cmp-booking-departure-placeholder', 'From')}
        state={state.focusedInput === START_DATE ? 'focus' : undefined}
        rightIcon="calendar-material"
        rightIconSize={20}
        disabled={getDisabledDepartureStatus()}
        ref={startRef}
        hasError={props.startDateHasError}
        errorMessage={props.startDateErrorMessage}
        active={Boolean(state.startDate)}
        onChange={(evt) => {
          const isEmpty = evt.target.value.trim() === '';
          const date = isEmpty ? null : parse(evt.target.value, dateFormatOnEdit);
          // FIXME: Remove bypassing null check
          if (isEmpty || (isValid(date) && !isDateBlocked(date!!))) {
            handleDateChange({
              startDate: date,
              endDate: state.endDate,
              focusedInput: START_DATE,
              shouldUpdate: false
            });
          }
        }}
        onClick={() => {
          if (isMobile) {
            setDialogOpen(true);
          } else {
            popup.ref.current?.open();
          }
        }}
        onFocus={() => {
          setStartInput(state.startDate, true);
          if (!isMobile) {
            requestFocus.current = 'start';
          }
          setState({ ...state, focusedInput: START_DATE });
          if (state.startDate !== null) {
            goToDate(state.startDate);
          } else {
            const date = getMinBlackoutDate(
              minDate,
              blackoutDatesIntegration,
              fetchBlackoutDates,
              START_DATE
            );
            if (blackoutDatesIntegration === true && date) {
              goToDate(date);
            } else {
              goToDate(minDate);
            }
          }
        }}
        onBlur={() => {
          if (!isMobile) {
            setTimeout(() => {
              if (requestFocus.current === false || requestFocus.current === 'start') {
                popup.ref.current?.close();
                setState({ ...state, focusedInput: null });
              }
              requestFocus.current = false;
            }, 200);
          }
          setStartInput(state.startDate, false);
        }}
        onKeyDown={(e) => {
          if (props?.onKeyDownHandleEnterAction) {
            props?.onKeyDownHandleEnterAction(e);
          }
        }}
      />
      <VisuallyHidden id={departureDescriptionId}>
        {translate(
          'cmp-datepicker-departure-description',
          'Type the departure date in the format {format}',
          { format: dateFormatToDescribe }
        )}
      </VisuallyHidden>
      {props.showTimePickers && (
        <Listbox
          options={props.timeOptions ?? []}
          onChange={(value) => setState({ ...state, startTime: value })}
          label={translate('cmp-booking-timepicker-label', 'Time')}
          layout="closed"
          defaultValue={props.timeOptions?.[0].value}
        />
      )}
      <Box
        sx={{
          display: props.singleSelection && props.UNSTABLE_hideDisabledInput ? 'none' : undefined
        }}
      >
        <Input
          label={props.labels?.return || 'Return'}
          aria-describedby={returnDescriptionId}
          placeholder={translate('cmp-booking-return-placeholder', 'To')}
          value={state.endDate && !props.singleSelection ? format(state.endDate, dateFormat) : ''}
          state={state.focusedInput === END_DATE ? 'focus' : undefined}
          rightIcon="calendar-material"
          rightIconSize={20}
          ref={endRef}
          hasError={props.endDateHasError}
          errorMessage={props.endDateErrorMessage}
          disabled={getDisabledReturnStatus()}
          active={Boolean(state.endDate)}
          onChange={(evt) => {
            const isEmpty = evt.target.value.trim() === '';
            const date = isEmpty ? null : parse(evt.target.value, dateFormatOnEdit);
            // FIXME: Remove bypassing null check
            if (
              isEmpty ||
              (isValid(date) &&
                !isDateBlocked(date!!) &&
                (!state.startDate || differenceInCalendarDays(date!!, state.startDate) >= 0))
            ) {
              handleDateChange({
                startDate: state.startDate,
                endDate: date,
                focusedInput: END_DATE,
                shouldUpdate: false
              });
            }
          }}
          onClick={() => {
            if (isMobile) {
              setDialogOpen(true);
            } else {
              popup.ref.current?.open();
            }
          }}
          onFocus={() => {
            setEndInput(state.endDate, true);
            if (!isMobile) {
              requestFocus.current = 'end';
            }
            setState({ ...state, focusedInput: END_DATE });
            if (state.endDate) {
              goToDate(state.endDate);
            } else {
              const date = getMinBlackoutDate(
                minDate,
                blackoutDatesIntegration,
                fetchBlackoutDates,
                END_DATE
              );
              if (blackoutDatesIntegration === true && date) {
                goToDate(date);
              } else {
                goToDate(minDate);
              }
            }
          }}
          onBlur={() => {
            if (!isMobile) {
              setTimeout(() => {
                if (requestFocus.current === false || requestFocus.current === 'end') {
                  popup.ref.current?.close();
                  setState({ ...state, focusedInput: null });
                }
                requestFocus.current = false;
              }, 200);
            }
            setEndInput(state.endDate, false);
          }}
          onKeyDown={(e) => {
            if (props?.onKeyDownHandleEnterAction) {
              props?.onKeyDownHandleEnterAction(e);
            }
          }}
        />
        <VisuallyHidden id={returnDescriptionId}>
          {translate(
            'cmp-datepicker-return-description',
            'Type the return date in the format {format}',
            { format: dateFormatToDescribe }
          )}
        </VisuallyHidden>
      </Box>
      {props.showTimePickers && (
        <Listbox
          options={props.timeOptions ?? []}
          onChange={(value) => setState({ ...state, endTime: value })}
          label={translate('cmp-booking-timepicker-label', 'Time')}
          layout="closed"
          defaultValue={props.timeOptions?.[0].value}
        />
      )}
    </Tiles>
  );

  const footer = (
    <Fragment>
      {props.showFlexibleDates ? (
        <Checkbox
          checked={state.isFlexibleDates}
          onChange={(evt) => {
            setState({
              ...state,
              isFlexibleDates: evt.target.checked
            });
          }}
          label={translate('cmp-booking-flexible-dates-label', 'Flexible dates')}
        />
      ) : null}
      <Inline align="end" sx={{ flex: '1' }}>
        <Button
          variant="text"
          disabled={state.startDate === null && state.endDate === null}
          onClick={() => {
            setMakeClearAction(true);
            setState(
              produce(state, (draft) => {
                draft.startDate = null;
                draft.endDate = null;
                draft.focusedInput = START_DATE;
              })
            );
            setActiveDone(true);
            props.setSelectedDates?.({ startDate: null, endDate: null });
            onResetDates();
            const date = getMinBlackoutDate(
              minDate,
              blackoutDatesIntegration,
              fetchBlackoutDates,
              START_DATE
            );
            if (blackoutDatesIntegration === true && date) {
              goToDate(date);
            } else {
              goToDate(minDate);
            }
          }}
        >
          {translate('cmp-booking-datepicker-clear-label', 'Clear')}
        </Button>
        <Button
          variant="text"
          disabled={
            makeClearAction
              ? true
              : activeDone
              ? false
              : props?.singleSelection
              ? state.startDate === null
              : state.startDate === null || state.endDate === null
          }
          onClick={() => {
            if (isMobile) {
              save();
            } else {
              setStartInput(state.startDate, false);
              setEndInput(state.endDate, false);
              props.setSelectedDates?.({ startDate: state.startDate, endDate: state.endDate });
              setState((state) =>
                produce(state, (draft) => {
                  draft.focusedInput = null;
                })
              );
              requestFocus.current = false;
              popup.ref.current?.close();
            }
            setActiveDone(false);
            if (props?.trigger) {
              props?.trigger('dates');
            }
          }}
        >
          {translate('cmp-booking-datepicker-done-label', 'Done')}
        </Button>
      </Inline>
    </Fragment>
  );
  const navigation = (
    <Inline
      align="space-between"
      pr="4"
      pl="4"
      sx={{
        position: 'absolute',
        width: '100%',
        top: '20px',
        variant: 'layout.datepickerStyles.navigation'
      }}
    >
      <Button
        variant="iconRounded"
        aria-label={translate('cmp-datepicker-button-previous-label', 'Previous Month')}
        disabled={isDisabledPreviousBtn(months[0].month, months[0].year)}
        onClick={() => {
          goToPreviousMonthsByOneMonth();
          if (state.focusedInput === START_DATE) {
            setStartInput(state.startDate, true);
          } else if (state.focusedInput === END_DATE) {
            setEndInput(state.endDate, true);
          }
        }}
      >
        <Icon
          icon="left-chevron"
          size={14}
          fill="text"
          style={{ stroke: '#4a4a4a', strokeWidth: '120px' }}
        />
      </Button>
      <Button
        variant="iconRounded"
        disabled={isDisabledNextBtn(
          monthsToShow === 2 ? months[0].month : months[monthsToShow - 1].month,
          monthsToShow === 2 ? months[0].year : months[monthsToShow - 1].year
        )}
        aria-label={translate('cmp-datepicker-button-next-label', 'Next Month')}
        onClick={() => {
          goToNextMonthsByOneMonth();
          if (state.focusedInput === START_DATE) {
            setStartInput(state.startDate, true);
          } else if (state.focusedInput === END_DATE) {
            setEndInput(state.endDate, true);
          }
        }}
      >
        <Icon
          icon="right-chevron"
          size={14}
          fill="text"
          style={{ stroke: '#4a4a4a', strokeWidth: '120px' }}
        />
      </Button>
    </Inline>
  );

  const content = (
    <Tiles columns={isMobile ? 1 : months.length} gap="4" p="4">
      {months.map((month, index) => {
        const isSelectedMonth =
          month.month === state.startDate?.getMonth() &&
          month.year === state.startDate.getFullYear();
        return (
          <Month
            ref={isSelectedMonth ? monthRef : undefined}
            dayFormat={props.dayFormat ?? 'd'}
            key={`${month.year}-${month.month}`}
            year={month.year}
            month={month.month}
            firstDayOfWeek={firstDayOfWeek}
            blackoutDatesIntegration={blackoutDatesIntegration}
            fetchBlackoutDates={fetchBlackoutDates}
            focusedInput={state.focusedInput}
          />
        );
      })}
    </Tiles>
  );

  React.useImperativeHandle(
    ref,
    () => ({
      handleEnterAction: () => {
        popup?.ref?.current?.close();
        if (startRef.current) {
          startRef.current.value = state.startDate ? format(state.startDate, getFormat(false)) : '';
        }
        if (endRef.current) {
          endRef.current.value = state.endDate ? format(state.endDate, getFormat(false)) : '';
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popup.ref, state]
  );

  useEffect(() => {
    if (isMobile && isDialogOpen) {
      setMakeClearAction(false);
      setTimeout(() => {
        if (monthRef && monthRef.current) {
          monthRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 250);
    }
  }, [isMobile, isDialogOpen]);

  const inputsToShow = isMobile && isDialogOpen ? null : inputs;

  return (
    <DatePickerContext.Provider
      value={{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
        isStartDate,
        isEndDate
      }}
    >
      <div ref={ref}>
        {isMobile ? (
          <Fragment>
            {inputsToShow}
            {isDialogOpen && (
              <div onScroll={handleScroll}>
                <MobileFilterDialog
                  label={translate('cmp-datepicker-dialog-title', 'Date Range')}
                  onDismiss={dismiss}
                >
                  <MobileHeader
                    title={translate('cmp-datepicker-header-title', 'Filters')}
                    onBackButtonPress={dismiss}
                  />
                  <Box p="2">{inputs}</Box>
                  <MobileBody>{content}</MobileBody>
                  <MobileFooter align="space-between">{footer}</MobileFooter>
                </MobileFilterDialog>
              </div>
            )}
          </Fragment>
        ) : (
          <Dropdown
            toggleElement={inputs}
            ref={popup.ref}
            targetRef={popup.filterRef}
            activeTab={props.activeTab}
            currentTab={props.currentTab}
            onClose={() => {
              setStartInput(state.startDate, false);
              setEndInput(state.endDate, false);
              props.setSelectedDates?.({ startDate: state.startDate, endDate: state.endDate });
              setState((state) =>
                produce(state, (draft) => {
                  draft.focusedInput = null;
                })
              );
              requestFocus.current = false;
              popup.ref.current?.close();
              setActiveDone(false);
            }}
          >
            <div
              onClick={() => {
                requestFocus.current = 'popup';
              }}
            >
              {navigation}
              {content}
              <Inline p="2" variant="separators.default" align="space-between">
                {footer}
              </Inline>
            </div>
          </Dropdown>
        )}
      </div>
    </DatePickerContext.Provider>
  );
}

const DatePicker = React.forwardRef(DatePickerCmp);

export { DatePicker };
